/**
 * Utility functions for the application
 * All utility functions should be placed here to avoid scattered utility files
 */

/*
|--------------------------------------------------------------------------
| DATE HELPERS
|--------------------------------------------------------------------------
*/

const DATE_LOCALES: Record<string, string> = {
  en: 'en-US',
};

/**
 * Get the locale string for date formatting
 * @param lang - Language code
 * @returns Locale string for Intl APIs
 */
export const getDateLocale = (lang: string): string => {
  return DATE_LOCALES[lang] || lang;
};

/**
 * Convert a date string to UTC Date object
 * @param dateStr - Date string to convert
 * @returns UTC Date object
 */
export const utc = (dateStr: string): Date => {
  return new Date(new Date(dateStr).toUTCString().substring(0, 25));
};

/**
 * Format a Date object or date string to YYYY-MM-DD using UTC methods.
 * Avoids timezone issues by using getUTCFullYear, getUTCMonth, getUTCDate.
 * @param date - Date object or ISO date string (e.g., '2026-02-19' or '2026-02-19T00:00:00.000Z')
 * @returns Date string in YYYY-MM-DD format
 * @example
 * ```ts
 * formatToUTCDate(new Date('2026-02-19')) // '2026-02-19'
 * formatToUTCDate('2026-02-19T00:00:00.000Z') // '2026-02-19'
 * ```
 */
export const formatToUTCDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    // Already a string - extract just the date part (before T) if it has time component
    return date.split('T')[0];
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Normalize a date string into YYYY-MM-DD when possible.
 * If the input is already in YYYY-MM-DD format, it is returned unchanged.
 * @param dateString - Input date string
 * @returns Simple date string when convertible; otherwise the original input
 */
export const formatToSimpleDate = (dateString: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  const date = new Date(dateString);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }

  return dateString;
};

/**
 * Mapping of locales to their IANA timezone identifiers.
 * Used for converting studio local times to UTC.
 */
const LOCALE_TIMEZONES: Record<string, string> = {
  'en-us': 'America/Los_Angeles', // PST/PDT
  'en-ca': 'America/Toronto', // EST/EDT
  'fr-ca': 'America/Toronto', // EST/EDT
  'de-de': 'Europe/Berlin', // CET/CEST
  'nl-nl': 'Europe/Amsterdam', // CET/CEST
  'nb-no': 'Europe/Oslo', // CET/CEST
  'de-ch': 'Europe/Zurich', // CET/CEST
  'fr-ch': 'Europe/Zurich', // CET/CEST
  'en-sa': 'Asia/Riyadh', // AST (UTC+3)
  'ar-sa': 'Asia/Riyadh', // AST (UTC+3)
  'en-ae': 'Asia/Dubai', // GST (UTC+4)
  'ar-ae': 'Asia/Dubai', // GST (UTC+4)
};

/**
 * Get the IANA timezone for a locale
 * @param locale - Locale string (e.g., 'de-de', 'en-us')
 * @returns IANA timezone identifier
 */
export const getTimezoneForLocale = (locale: string): string => {
  if (!locale) return 'Europe/Berlin';
  return LOCALE_TIMEZONES[locale.toLowerCase()] || 'America/Los_Angeles';
};

/**
 * Combines a date string with hours and minutes into an OffsetDateTime string.
 * The time is interpreted as the studio's local time (based on locale) and converted to UTC.
 * @param dateStr - Date in YYYY-MM-DD format
 * @param hours - Hour (0-23) in studio local time
 * @param minutes - Minutes (0-59)
 * @param locale - Locale to determine studio timezone (e.g., 'de-de' → Europe/Berlin)
 * @returns OffsetDateTime string in UTC (e.g., '2026-02-12T11:00:00+00:00')
 * @example
 * ```ts
 * // German studio (CET = UTC+1) at 12:00 local time
 * localToUTC('2026-02-12', 12, 0, 'de-de') // '2026-02-12T11:00:00+00:00'
 *
 * // US studio (PST = UTC-8) at 12:00 local time
 * localToUTC('2026-02-12', 12, 0, 'en-us') // '2026-02-12T20:00:00+00:00'
 * ```
 */
export const localToUTC = (
  dateStr: string,
  hours: number,
  minutes: number,
  locale = 'en-us'
): string => {
  const timezone = getTimezoneForLocale(locale);

  // Use Intl.DateTimeFormat to get the UTC offset for this timezone at this date/time
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Create a "guess" date in UTC, then adjust to find the correct UTC time
  const [year, month, day] = dateStr.split('-').map(Number);

  // Start with the local time as if it were UTC
  const utcGuess = Date.UTC(year, month - 1, day, hours, minutes, 0, 0);

  // Format this UTC time in the target timezone to see what local time it shows
  const parts = formatter.formatToParts(new Date(utcGuess));
  const getPart = (type: string) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10);

  const guessLocalHour = getPart('hour');
  const guessLocalMinute = getPart('minute');
  const guessLocalDay = getPart('day');

  // Calculate the difference between the target local time and what we got
  let hourDiff = hours - guessLocalHour;
  let dayDiff = day - guessLocalDay;

  // Handle day boundary (e.g., if we wanted 2:00 AM but got 11:00 PM previous day)
  if (dayDiff > 15) dayDiff = dayDiff - 31; // End of month wrap
  if (dayDiff < -15) dayDiff = dayDiff + 31; // Start of month wrap

  hourDiff += dayDiff * 24;
  const minuteDiff = minutes - guessLocalMinute;

  // Adjust our UTC guess by the difference
  const offsetMs = (hourDiff * 60 + minuteDiff) * 60 * 1000;
  const utcDate = new Date(utcGuess + offsetMs);

  // Convert to OffsetDateTime format
  return utcDate.toISOString().replace(/\.\d{3}Z$/, '+00:00');
};

/**
 * Format a Date object as OffsetDateTime string (ISO 8601 format without milliseconds).
 * Useful when the backend expects OffsetDateTime format.
 * @param date - The date to format
 * @returns Formatted date string in the format: YYYY-MM-DDTHH:mm:ss+00:00
 * @example
 * ```ts
 * formatToOffsetDateTime(new Date()) // '2025-12-19T15:23:19+00:00'
 * ```
 */
export const formatToOffsetDateTime = (date: Date): string => {
  return date.toISOString().replace(/\.\d{3}Z$/, '+00:00');
};

/**
 * Extract hours and minutes from a UTC datetime string, converted to local studio time.
 * This is the inverse of localToUTC - converts UTC back to studio local time for display.
 * @param datetime - ISO datetime string in UTC (e.g., '2026-02-12T11:00:00+00:00')
 * @param locale - Locale to determine studio timezone (e.g., 'de-de' → Europe/Berlin)
 * @returns Object with hour and minute in studio local time
 * @example
 * ```ts
 * // 11:00 UTC displayed for German studio (CET = UTC+1)
 * getLocalTimeFromUTC('2026-02-12T11:00:00+00:00', 'de-de') // { hour: 12, minute: 0 }
 *
 * // 20:00 UTC displayed for US studio (PST = UTC-8)
 * getLocalTimeFromUTC('2026-02-12T20:00:00+00:00', 'en-us') // { hour: 12, minute: 0 }
 * ```
 */
export const getLocalTimeFromUTC = (
  datetime: string | undefined | null,
  locale = 'en-us'
): { hour: number; minute: number } | null => {
  if (!datetime) return null;

  try {
    const timezone = getTimezoneForLocale(locale);
    const date = new Date(datetime);

    // Use Intl.DateTimeFormat to get the time in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);

    return { hour, minute };
  } catch {
    return null;
  }
};

/**
 * Format a time slot range from UTC datetime strings to studio local time.
 * Converts UTC times back to the studio's local timezone for display.
 * @param startDatetime - ISO datetime string in UTC for start
 * @param endDatetime - ISO datetime string in UTC for end
 * @param locale - Locale to determine studio timezone
 * @returns Time range string matching dropdown options (e.g., "12:00-12:30")
 * @example
 * ```ts
 * // 11:00-11:30 UTC displayed for German studio (CET = UTC+1)
 * utcToLocalTimeSlot('2026-02-12T11:00:00+00:00', '2026-02-12T11:30:00+00:00', 'de-de')
 * // Returns "12:00-12:30"
 * ```
 */
export const utcToLocalTimeSlot = (
  startDatetime?: string,
  endDatetime?: string,
  locale = 'en-us'
): string => {
  if (!startDatetime || !endDatetime) return '';

  const start = getLocalTimeFromUTC(startDatetime, locale);
  const end = getLocalTimeFromUTC(endDatetime, locale);

  if (!start || !end) return '';

  const formatTime = (hour: number, minute: number): string => {
    const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    const m = minute.toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  return `${formatTime(start.hour, start.minute)}-${formatTime(end.hour, end.minute)}`;
};

/**
 * Extract date part from datetime string for date picker components.
 * @param datetime - ISO datetime string or any valid date string
 * @returns Date string in format YYYY-MM-DD or empty string if invalid
 * @example
 * ```ts
 * formatDateForPicker('2024-12-19T15:23:19+00:00') // '2024-12-19'
 * formatDateForPicker(null) // ''
 * ```
 */
export const formatDateForPicker = (datetime?: string | null): string => {
  if (!datetime) return '';
  try {
    const date = new Date(datetime);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Extract date part from UTC datetime string, converted to studio local date.
 * This handles the case where UTC date differs from local date (e.g., 6 PM PST = 2 AM UTC next day).
 * @param datetime - ISO datetime string in UTC (e.g., '2026-02-13T02:00:00+00:00')
 * @param locale - Locale to determine studio timezone (e.g., 'en-us' → America/Los_Angeles)
 * @returns Date string in format YYYY-MM-DD in studio local timezone
 * @example
 * ```ts
 * // 6 PM PST on Feb 12 stored as 2 AM UTC on Feb 13
 * utcToLocalDate('2026-02-13T02:00:00+00:00', 'en-us') // '2026-02-12'
 *
 * // Noon CET on Feb 12 stored as 11 AM UTC on Feb 12
 * utcToLocalDate('2026-02-12T11:00:00+00:00', 'de-de') // '2026-02-12'
 * ```
 */
export const utcToLocalDate = (datetime?: string | null, locale = 'en-us'): string => {
  if (!datetime) return '';

  try {
    const timezone = getTimezoneForLocale(locale);
    const date = new Date(datetime);

    // Use Intl.DateTimeFormat to get the date in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    // en-CA locale formats as YYYY-MM-DD which is what we need
    return formatter.format(date);
  } catch {
    return '';
  }
};

/**
 * Format a date string using Date#toLocaleString.
 * Use `language` to control the display language independently from the locale
 * (e.g., render English month names while keeping locale-specific formatting rules).
 * @param dateStr - The date string to format
 * @param locale - Locale for formatting rules (default: 'en-US')
 * @param options - Intl.DateTimeFormatOptions (note: when `timeStyle` is provided, default date parts are omitted)
 * @param language - Display language override (e.g., 'en' to render in English regardless of locale)
 * @returns Formatted date string or '-' if invalid
 * @example
 * ```ts
 * formatDate('2024-01-15') // "Jan 15, 2024"
 * formatDate('2024-01-15', 'de-DE') // "15. Jan. 2024"
 * formatDate('2024-01-15', 'de-DE', {}, 'en') // "Jan 15, 2024" (English display, DE formatting)
 * formatDate(null) // "-"
 * ```
 */
export const formatDate = (
  dateStr: string | null | undefined,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
  language?: string
): string => {
  if (!dateStr) return '-';

  const date = utc(dateStr);
  if (isNaN(date.getTime())) return '-';

  // w20-13562 – force en-sa to use en-us
  const resolvedLocale = locale.toLowerCase() === 'en-sa' ? 'en-US' : locale;

  // Use language for display (month names etc.), fall back to locale
  const displayLocale = language || getDateLocale(resolvedLocale);

  // timeStyle cannot be combined with individual date/time options per toLocaleString docs
  const formatOptions: Intl.DateTimeFormatOptions =
    'timeStyle' in options
      ? options
      : {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          ...options,
        };

  return date.toLocaleString(displayLocale, formatOptions);
};

/**
 * Format a date string with ordered format (day month year)
 * Used for specific locales like en-sa that need "07 December 2023" format
 * @param dateStr - The date string to format
 * @param locale - Locale for formatting
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const orderedFormat = (
  dateStr: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = new Date(utc(dateStr));
  if (locale === 'en-sa') {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', options);
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } else {
    return formatDate(dateStr, locale, options);
  }
};

/**
 * Normalizes truthy flags that may come from API payloads as booleans or strings.
 * Accepts `true` and case-insensitive `'true'`; all other values are false.
 * @param value - Unknown flag value to evaluate
 * @returns Whether the value should be treated as true
 */
export const isTruthyFlag = (value: unknown): boolean => {
  if (value === true) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return false;
};

/**
 * Type guard for Object.entries values that are not null/undefined,
 * with optional key exclusions.
 *
 * @param entry - Key-value tuple from Object.entries
 * @param excludedKeys - Optional set of keys to exclude
 * @returns Whether the entry value is non-nullish and key is not excluded
 */
export const isNonNullableEntry = <TValue>(
  entry: [string, TValue | null | undefined],
  excludedKeys?: ReadonlySet<string>
): entry is [string, TValue] => {
  const [key, value] = entry;
  return value !== null && value !== undefined && !excludedKeys?.has(key);
};

/**
 * Format a date string to a human-readable date and time format.
 * Use `language` to control the display language independently from the locale
 * (e.g., render English month names while keeping 24-hour format from a non-US locale).
 * @param dateString - The date string to format (ISO format or any valid date string)
 * @param locale - Locale for formatting rules like 24h clock (default: 'en-US')
 * @param language - Display language override (e.g., 'en' to render in English regardless of locale)
 * @returns Formatted date string like "Dec 18, 2024, 03:30 PM" or '-' if invalid
 * @example
 * ```ts
 * formatDateTime('2024-12-18T15:30:00Z') // "Dec 18, 2024, 03:30 PM"
 * formatDateTime('2024-12-18T15:30:00Z', 'de-DE') // "18. Dez. 2024, 15:30"
 * formatDateTime('2024-12-18T15:30:00Z', 'de-DE', 'en') // "Dec 18, 2024, 15:30"
 * formatDateTime(null) // "-"
 * ```
 */
export const formatDateTime = (
  dateString: string | null | undefined,
  locale = 'en-US',
  language?: string
): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  // Use 24-hour format for non-US locales
  const use24Hour = !locale.toLowerCase().startsWith('en-us');

  // Use language for display (month names etc.), fall back to locale
  const displayLocale = language || getDateLocale(locale);

  return date.toLocaleString(displayLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: !use24Hour,
  });
};

/**
 * Calculate the number of days between a date and now
 * @param start - Start date string
 * @returns Number of days (negative if in the past, positive if in the future)
 * @example
 * ```ts
 * getDaysUntilNow('2024-12-25') // days until Christmas
 * ```
 */
export const getDaysUntilNow = (start: string): number => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const then = new Date(start).setHours(0, 0, 0, 0);
  const now = new Date().setHours(0, 0, 0, 0);
  const daysDifference = (then - now) / MS_PER_DAY;
  return daysDifference;
};

/*
|--------------------------------------------------------------------------
| LOCALE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Converts URL-friendly locale format (lowercase) to Contentful format
 * @param locale - Locale in format like 'en-us', 'de-de', 'fr-ca'
 * @returns Locale in Contentful format like 'en-US', 'de-DE', 'fr-CA'
 * @example
 * ```ts
 * convertToContentfulLocale('en-us') // 'en-US'
 * convertToContentfulLocale('de-de') // 'de-DE'
 * ```
 */
export const convertToContentfulLocale = (locale: string): string => {
  if (!locale || !locale.includes('-')) return 'de-DE';
  const [language, country] = locale.split('-');
  return `${language}-${country.toUpperCase()}`;
};

/**
 * Formats locale for API calls by converting to underscore format
 * Adds '_us' suffix for 2-character locales
 * @param locale - Locale in format like 'en', 'en-us', 'nl-nl'
 * @returns Locale in API format like 'en_us', 'en_us', 'nl_nl'
 * @example
 * ```ts
 * formatLocaleForApi('en') // 'en_us'
 * formatLocaleForApi('en-us') // 'en_us'
 * formatLocaleForApi('nl-nl') // 'nl_nl'
 * ```
 */
export const formatLocaleForApi = (locale: string): string => {
  if (!locale) return 'de_de';
  if (locale.length === 2) return `${locale}_us`;
  return locale.replace('-', '_');
};

/**
 * Extract ISO 3166-1 alpha-2 country code from locale
 * @param locale - Locale string (e.g., 'en-us', 'de-de', 'nl-nl')
 * @returns ISO 3166-1 alpha-2 country code (e.g., 'US', 'DE', 'NL')
 * @example
 * getCountryCodeFromLocale('en-us') // 'US'
 * getCountryCodeFromLocale('de-de') // 'DE'
 * getCountryCodeFromLocale('en') // 'US'
 */
export const getCountryCodeFromLocale = (locale: string): string => {
  if (!locale) return 'DE';
  const normalizedLocale = locale.toLowerCase();

  // Handle special cases
  if (normalizedLocale === 'en' || normalizedLocale === 'en-us') return 'US';

  // Extract country code from locale format (e.g., 'de-de' -> 'DE')
  const parts = normalizedLocale.split('-');
  if (parts.length === 2) {
    return parts[1].toUpperCase();
  }

  // Fallback to DE (default market) if cannot determine
  return 'DE';
};

/*
|--------------------------------------------------------------------------
| LOCAL STORAGE HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Get an item from localStorage with JSON parsing
 * @param key - The storage key
 * @returns The parsed value or null if not found/invalid
 * @example
 * ```ts
 * getStorageItem<UserIdentity>('user-identity')
 * ```
 */
export const getStorageItem = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item) as T;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

/**
 * Set an item in localStorage with JSON stringification
 * @param key - The storage key
 * @param value - The value to store
 * @example
 * ```ts
 * setStorageItem('user-identity', userIdentity)
 * ```
 */
export const setStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Remove an item from localStorage
 * @param key - The storage key
 * @example
 * ```ts
 * removeStorageItem('user-identity')
 * ```
 */
export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Formats a string by replacing hyphens and underscores with spaces
 * and capitalizing the first letter of each word (Title Case)
 * @param str - The string to format
 * @returns Formatted string in Title Case
 * @example
 * ```ts
 * formatString('destination_charge') // "Destination Charge"
 * formatString('add-ons') // "Add Ons"
 * formatString('total price') // "Total Price"
 * formatString('hello') // "Hello"
 * ```
 */
export const formatString = (str: string | null | undefined): string => {
  if (!str) return '';
  return str
    .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get the currency code for a given locale
 * @param locale - The locale string (e.g., 'en-us', 'de-de')
 * @returns ISO 4217 currency code
 */
export const getCurrencyCodeForLocale = (locale: string): string => {
  if (!locale) return 'EUR';
  const normalizedLocale = locale.toLowerCase();
  switch (normalizedLocale) {
    case 'en':
    case 'en-us':
      return 'USD';
    case 'en-ca':
    case 'fr-ca':
      return 'CAD';
    case 'de-de':
    case 'nl-nl':
      return 'EUR';
    case 'nb-no':
      return 'NOK';
    case 'ar-sa':
    case 'en-sa':
      return 'SAR';
    case 'en-ae':
    case 'ar-ae':
      return 'AED';
    case 'fr-ch':
    case 'de-ch':
      return 'CHF';
    default:
      return 'USD';
  }
};

/**
 * Get currency symbol for a currency code
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency symbol
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    CAD: '$',
    EUR: '€',
    NOK: 'kr',
    SAR: 'ر.س',
    AED: 'د.إ',
    CHF: 'CHF',
  };
  return symbols[currencyCode] || currencyCode;
};

export type CurrencyDetails = {
  currencyCode: string;
  currencySymbol: string;
  prefix: string;
  prefixSpace: boolean;
  suffix: string;
  fractionalSeparator: string;
};

/**
 * Get detailed currency formatting information for a locale
 * @param locale - The locale string
 * @returns Currency formatting details
 */
export const getCurrencyDetailsForLocale = (locale: string): CurrencyDetails => {
  if (!locale) return getCurrencyDetailsForLocale('de-de');
  const normalizedLocale = locale.toLowerCase();
  const code = getCurrencyCodeForLocale(normalizedLocale);
  const symbol = getCurrencySymbol(code);

  let prefix = '';
  let prefixSpace = false;
  let suffix = '';
  let fractionalSeparator = '.';

  switch (normalizedLocale) {
    case 'en':
    case 'en-us': // $123,456.78
      prefix = symbol;
      prefixSpace = false;
      fractionalSeparator = '.';
      break;
    case 'en-ca': // $123,456.78 CAD
      prefix = symbol;
      prefixSpace = false;
      suffix = code;
      fractionalSeparator = '.';
      break;
    case 'fr-ca': // 123 456,78 CAD
      suffix = code;
      fractionalSeparator = ',';
      break;
    case 'de-de': // 123.456,78 €
      suffix = symbol;
      fractionalSeparator = ',';
      break;
    case 'nl-nl': // €123.456,78
      prefix = symbol;
      prefixSpace = false;
      fractionalSeparator = ',';
      break;
    case 'nb-no': // kr 123 456,78
      prefix = symbol;
      prefixSpace = true;
      fractionalSeparator = ',';
      break;
    case 'de-ch': // CHF 123'456.78
      prefix = code;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'fr-ch': // 123 456.78 CHF
      suffix = code;
      fractionalSeparator = '.';
      break;
    case 'en-sa': // SAR 123,456.78
      prefix = code;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'ar-sa': // ر.س 123,456.78
      prefix = symbol;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'en-ae': // AED 123,456.78
      prefix = code;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'ar-ae': // د.إ 123,456.78
      prefix = symbol;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    default:
      prefix = symbol;
      prefixSpace = false;
      fractionalSeparator = '.';
      break;
  }

  return {
    currencyCode: code,
    currencySymbol: symbol,
    prefix,
    prefixSpace,
    suffix,
    fractionalSeparator,
  };
};

export type FormattedPrice = {
  locale: string;
  integerAmount: string;
  fractionalAmount: string;
  formattedPriceWithCents: string;
  formattedPriceWithoutCents: string;
  priceWithCents: string;
  priceWithoutCents: string;
  currencyCode: string;
  currencySymbol: string;
};

type LocalePriceValue = string | number | undefined | null;

/**
 * Parses a numeric string that may be locale-formatted into a JavaScript number.
 * Handles both raw dot-decimal strings ("5488.6") and locale-formatted strings
 * ("5.488,6" for de-DE or "5,488.6" for en-US).
 * @param value - A numeric string, potentially locale-formatted
 * @returns The parsed number, or 0 if the value is empty/unparseable
 * @example
 * ```ts
 * parseLocalePrice('5488.6')    // 5488.6  (raw dot-decimal)
 * parseLocalePrice('5.488,6')   // 5488.6  (de-DE formatted)
 * parseLocalePrice('5,488.6')   // 5488.6  (en-US formatted)
 * parseLocalePrice('1.234.567') // 1234567 (de-DE thousands only)
 * parseLocalePrice('')          // 0
 * ```
 */
export const parseLocalePrice = (value: LocalePriceValue): number => {
  if (!value) return 0;

  if (typeof value === 'number') return value;

  // If it's already a valid JS number, use it directly
  const direct = Number(value);
  if (!Number.isNaN(direct)) return direct;

  // Strip everything except digits, dots, commas, and leading minus
  const cleaned = value.replace(/[^\d.,-]/g, '');
  if (!cleaned) return 0;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');

  if (lastComma > lastDot) {
    // Comma is the decimal separator (e.g., "5.488,6" → de-DE)
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }

  // Dot is the decimal separator (e.g., "5,488.6") or no separator at all
  return parseFloat(cleaned.replace(/,/g, '')) || 0;
};

/**
 * Format a price according to locale-specific conventions (similar to lucid-website)
 * @param rawPrice - The raw price (number, string, or undefined)
 * @param locale - The locale string (e.g., 'en-us', 'de-de')
 * @returns Formatted price object with various formatting options
 * @example
 * ```ts
 * formatLocalePrice(123456.78, 'en-us')
 * // { formattedPriceWithCents: '$123,456.78 USD', formattedPriceWithoutCents: '$123,456 USD', ... }
 *
 * formatLocalePrice(123456.78, 'de-de')
 * // { formattedPriceWithCents: '123.456,78 €', formattedPriceWithoutCents: '123.457 €', ... }
 * ```
 */
export const formatLocalePrice = (
  rawPrice: string | number | null | undefined,
  locale: string
): FormattedPrice => {
  // Convert string to number, handle undefined
  const price = typeof rawPrice === 'string' ? parseFloat(rawPrice) || 0 : (rawPrice ?? 0);

  const normalizedLocale = (locale || 'de-de').toLowerCase();
  const localeCurrencyDetails = getCurrencyDetailsForLocale(normalizedLocale);

  const currencyFormatWithCents: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: localeCurrencyDetails.currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const currencyFormatWithoutCents: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: localeCurrencyDetails.currencyCode,
    maximumFractionDigits: 0,
  };

  let formattedPriceWithCents = '';
  let formattedPriceWithoutCents = '';
  let priceWithCents = '';
  let priceWithoutCents = '';

  // Get the locale string for Intl (convert our format to standard)
  const intlLocale = normalizedLocale === 'en' ? 'en-US' : normalizedLocale;

  switch (normalizedLocale) {
    case 'en':
    case 'en-us': // $123,456.78 USD
      formattedPriceWithCents = `${price.toLocaleString('en-US', currencyFormatWithCents)} USD`;
      formattedPriceWithoutCents = `${price.toLocaleString('en-US', currencyFormatWithoutCents)} USD`;
      priceWithCents = price.toLocaleString('en-US', currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString('en-US', currencyFormatWithoutCents);
      break;
    case 'en-ca': // $123,456.78 CAD
      formattedPriceWithCents = `${price.toLocaleString(intlLocale, currencyFormatWithCents)} CAD`;
      formattedPriceWithoutCents = `${price.toLocaleString(intlLocale, currencyFormatWithoutCents)} CAD`;
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    case 'fr-ca': // 123 456,78 CAD
      formattedPriceWithCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithCents,
        currencyDisplay: 'code',
      });
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithoutCents,
        currencyDisplay: 'code',
      });
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    case 'ar-sa': // ر.س 123,456.78
      formattedPriceWithCents = `${localeCurrencyDetails.currencySymbol} ${price.toLocaleString(
        'en-AE',
        {
          ...currencyFormatWithCents,
          style: 'decimal',
        }
      )}`;
      formattedPriceWithoutCents = `${localeCurrencyDetails.currencySymbol} ${price.toLocaleString(
        'en-AE',
        {
          ...currencyFormatWithoutCents,
          style: 'decimal',
        }
      )}`;
      priceWithCents = formattedPriceWithCents;
      priceWithoutCents = formattedPriceWithoutCents;
      break;
    case 'en-ae': // AED 123,456.78
      formattedPriceWithCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithCents,
        currencyDisplay: 'code',
      });
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithoutCents,
        currencyDisplay: 'code',
      });
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    case 'ar-ae': // د.إ 123,456.78
      formattedPriceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      priceWithCents = formattedPriceWithCents;
      priceWithoutCents = formattedPriceWithoutCents;
      break;
    case 'en-sa': // SAR 123,456.78
      formattedPriceWithCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithCents,
        currencyDisplay: 'code',
      });
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithoutCents,
        currencyDisplay: 'code',
      });
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    default: // de-de, nl-nl, nb-no, de-ch, fr-ch, etc.
      formattedPriceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      priceWithCents = formattedPriceWithCents;
      priceWithoutCents = formattedPriceWithoutCents;
      break;
  }

  // Get integer-only formatted number
  const integerAmount = price.toLocaleString(normalizedLocale === 'ar-sa' ? 'ar-SA' : intlLocale, {
    style: 'decimal',
    maximumFractionDigits: 0,
  });

  // Get cents/fractional part
  const fixedAmount = Number(price).toFixed(2);
  const fractionalAmount = fixedAmount.split('.')[1] || '00';

  return {
    locale: normalizedLocale,
    integerAmount,
    fractionalAmount,
    formattedPriceWithCents,
    formattedPriceWithoutCents,
    priceWithCents,
    priceWithoutCents,
    currencyCode: localeCurrencyDetails.currencyCode,
    currencySymbol: localeCurrencyDetails.currencySymbol,
  };
};

/**
 * Format a number as currency with proper locale formatting
 * @param value - The numeric value to format
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56")
 * @example
 * ```ts
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1000, 'de-DE') // "1.000,00 €"
 * ```
 */
export const formatCurrency = (
  value: string | number | null | undefined,
  locale = 'en-US'
): string => {
  // Use the new formatLocalePrice for consistent formatting
  const formatted = formatLocalePrice(value, locale);
  return formatted.priceWithCents;
};

/*
|--------------------------------------------------------------------------
| LOGGING UTILITIES
|--------------------------------------------------------------------------
*/

/**
 * Development-only logger that only logs in non-production environments
 * Prevents accidental logging of sensitive data in production
 * @param message - Message to log
 * @param data - Optional data to log
 * @example
 * ```ts
 * devLog('User authenticated', { userId: user.id });
 * ```
 */
export const devLog = (message: string, data?: unknown): void => {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.log(`[DEV] ${message}`, data);
    } else {
      console.log(`[DEV] ${message}`);
    }
  }
};

/**
 * Development-only warning logger
 * @param message - Warning message
 * @param data - Optional data to log
 */
export const devWarn = (message: string, data?: unknown): void => {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.warn(`[DEV] ${message}`, data);
    } else {
      console.warn(`[DEV] ${message}`);
    }
  }
};

/*
|--------------------------------------------------------------------------
| FILE DOWNLOAD HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Triggers a file download from a URL
 * Creates a temporary anchor element and clicks it to initiate the download.
 * @param url - The URL to download from (can be a blob URL, data URL, or remote URL)
 * @param filename - The filename to use for the download
 * @example
 * ```ts
 * // Download from a presigned URL
 * triggerLinkDownload('https://storage.example.com/file?token=abc', 'document.pdf');
 *
 * // Download from a blob URL
 * const blobUrl = URL.createObjectURL(blob);
 * triggerLinkDownload(blobUrl, 'export.csv');
 * ```
 */
export const triggerLinkDownload = (url: string, filename: string): void => {
  if (!url || !filename) return;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Triggers a file download from a Blob
 * @param blob - The Blob to download
 * @param filename - The filename to use for the download
 * @example
 * ```ts
 * const blob = new Blob([data], { type: 'application/pdf' });
 * triggerBlobDownload(blob, 'invoice.pdf');
 * ```
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  triggerLinkDownload(url, filename);
  window.URL.revokeObjectURL(url);
};
